/// <amd-module name="VSS/Platform/RestClientOptionsProvider" />
import { IAuthenticationService } from "./Authentication";
import {
    IRestClientOptionsProviderService,
    IVssPageContext,
    IVssRestClientOptions,
    IVssRestClientRegistrationOptions,
    Services,
    VssService
} from "./Context";
import { IPlatformRestErrorHandlerService } from "./IPlatformRestErrorHandler";
import { IVssLocationService, getServiceLocation } from "./Location";
import { IVssPageService } from "./Page";
import { ITokenProviderService } from "./TokenProvider";
import { UserClaims, userHasClaim } from "./UserClaims";

class RestClientOptionsProviderService extends VssService implements IRestClientOptionsProviderService {
    public populateRestClientOptions(
        registrationOptions: IVssRestClientRegistrationOptions | undefined,
        instanceOptions: IVssRestClientOptions | undefined
    ): IVssRestClientOptions {
        const options: IVssRestClientOptions = { pageContext: this.pageContext, errorHandler: restErrorHandler, ...instanceOptions };

        if (!options.resourceAreaId) {
            options.resourceAreaId = registrationOptions?.resourceAreaId;
        }

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData) {
            if (!options.sessionId) {
                options.sessionId = pageData.sessionId;
            }

            if (!options.rootPath) {
                const hostType = options.hostType;
                let serviceInstanceType = options.serviceInstanceType;

                if (!serviceInstanceType && registrationOptions) {
                    if (registrationOptions.resourceAreaId) {
                        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
                        serviceInstanceType = locationService.getServiceInstanceTypeForResourceArea(registrationOptions.resourceAreaId);
                    }
                    if (!serviceInstanceType) {
                        serviceInstanceType = registrationOptions.serviceInstanceType;
                    }
                }

                if (!options.authTokenProvider) {
                    // if no auth token provider was supplied, use the MSAL token provider
                    // by default, it will redirect to eSTS if the first request can't find any tokens
                    //      if subsequent requests can't find tokens (i.e. expired refresh token, or the user cleared something out) it'll initiate the popup flow
                    // supply a SilentMsalTokenProvider or a RedirectMsalTokenProvider to change this behavior
                    const tokenService = this.pageContext.getService<ITokenProviderService>("ITokenProviderService");
                    const tokenProvider = tokenService.getDefaultProvider();
                    if (!!tokenProvider) {
                        options.authTokenProvider = tokenProvider;
                    }
                }

                if (
                    (pageData.isHosted && serviceInstanceType && serviceInstanceType !== pageData.serviceInstanceType) ||
                    (hostType && hostType !== pageData.hostType)
                ) {
                    // Do not use any token if user can not issue auth token
                    if (pageData.isHosted && userHasClaim(this.pageContext, UserClaims.Member) && !options.authTokenProvider) {
                        // Use the default auth token provider for the request since we are talking to a different service.
                        const authService = this.pageContext.getService<IAuthenticationService>("IAuthenticationService");
                        options.authTokenProvider = authService.getDefaultTokenProvider();
                    }

                    options.rootPath = getServiceLocation(this.pageContext, serviceInstanceType, options.hostType);
                } else {
                    // We are talking back to the same service that served this page.
                    // In the hosted service, if options.authTokenProvider has not been set by this point, we will rely on cookies to authenticate the request.
                    options.rootPath = pageData.hostPath || "/";
                }
            }
        }

        return options;
    }
}

function restErrorHandler(pageContext: IVssPageContext, tries: number, text: string | undefined, response: Response | undefined, error: Error) {
    const handler = pageContext.getService<IPlatformRestErrorHandlerService>("IPlatformRestErrorHandlerService");
    return handler.handleError(tries, text, response, error);
}

Services.add("IRestClientOptionsProviderService", { serviceFactory: RestClientOptionsProviderService });
